import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutFrame, ListItem, ListTemplate, SearchBar, registerHomeTile } from '@room/shared-ui';
import { useToast } from '@room/shared-ui/components/UI';
import { Badge } from '@room/shared-ui/components/ui/badge';
import { Button } from '@room/shared-ui/components/ui/button';
import { adminAPI } from '../../../lib/api';
import styles from '../AdminPage.module.css';
import { adminTileVisible } from '../homeTiles';

type SystemUser = {
  id: string;
  username?: string;
  role?: string;
  created_at?: number | string;
  last_seen?: number | string | null;
};

registerHomeTile({
  id: 'home-admin-system-users',
  label: 'System Users',
  icon: '👥',
  route: '/admin/system-users',
  section: 'main',
  order: 90,
  visible: adminTileVisible,
});

const roleBadgeVariant = (role?: string) => {
  if (!role) return 'outline';
  if (role.includes('super')) return 'info';
  if (role.includes('system')) return 'success';
  return 'secondary';
};

export default function SystemUsersPage() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'system-users'],
    queryFn: () => adminAPI.getSystemUsers(),
  });

  const users = (data?.data?.users ?? []) as SystemUser[];

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) => {
      const haystack = [user.username, user.id, user.role].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [search, users]);

  const promoteMutation = useMutation({
    mutationFn: (userId: string) => adminAPI.promoteSystemUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'system-users'] });
      addToast('User promoted to system admin.', 'success');
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to promote user.';
      addToast(message, 'error');
    },
  });

  const demoteMutation = useMutation({
    mutationFn: (userId: string) => adminAPI.demoteSystemUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'system-users'] });
      addToast('User demoted to member.', 'success');
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to demote user.';
      addToast(message, 'error');
    },
  });

  return (
    <LayoutFrame withSection={false}>
      <ListTemplate<SystemUser>
        items={filteredUsers}
        loading={isLoading}
        error={error instanceof Error ? error.message : null}
        keyExtractor={(item) => item.id}
        useAppHeader
        appHeader={{
          title: 'System Users',
          subtitle: 'Manage admin roles and access for platform operators.',
        }}
        search={<SearchBar placeholder="Search users..." onSearch={setSearch} />}
        renderItem={(user) => {
          const role = user.role ?? 'user';
          const isAdmin = role !== 'user';
          return (
            <ListItem
              title={user.username || user.id}
              subtitle={`User ID: ${user.id}`}
              meta={user.last_seen ? `Last seen: ${new Date(user.last_seen).toLocaleString()}` : 'No activity yet'}
              badges={<Badge variant={roleBadgeVariant(role)}>{role}</Badge>}
              trailing={
                <div className={styles.listActions}>
                  {isAdmin ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => demoteMutation.mutate(user.id)}
                      loading={demoteMutation.isPending}
                    >
                      Demote
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => promoteMutation.mutate(user.id)}
                      loading={promoteMutation.isPending}
                    >
                      Promote
                    </Button>
                  )}
                </div>
              }
            />
          );
        }}
      />
    </LayoutFrame>
  );
}
